/* eslint-disable */
import { Injectable } from '@angular/core';
import { Util } from '@libs/utilities/util';
import { AbstractExtensionPattern } from '@libs/libraries/shared';

import { NgDragging } from '../shared/customizable-slider.data';
import { EnumNgPointerType } from '../shared/customizable-slider.interface';
import { NgSliderHandleDirective } from '../directives/slider-handle.directive';
import { NgCustomizableSliderComponent } from '../customizable-slider.component';


@Injectable()
export class NgSliderEventsExtension extends AbstractExtensionPattern<NgCustomizableSliderComponent> {

	/**------------------------------------------------------
	 * Event Handlers
	 */
	onStart(pointerType: EnumNgPointerType, event: MouseEvent|TouchEvent, bindMove: boolean, bindEnd: boolean, simulateImmediateMove?: boolean, simulateImmediateEnd?: boolean) {

		//0 - stop propagation event
		event.stopPropagation();
		this.main.moving = false;

		//1 - calculate view dimensions
		this.main.sliderValueUpdateExtension.calculateViewDimensions();

		//2 - update pointer type
		if (Util.Basic.isUndefined(pointerType)) pointerType = this.main.sliderHandlerExtension.getNearestHandle(event);
		this.main.currentTrackingPointer = pointerType;

		//3 - get pointer event active and focus
		const pointerElement: NgSliderHandleDirective = this.main.getPointerElement(pointerType);
		pointerElement.active = true;
		if (this.main.viewOptions.keyboardSupport) pointerElement.focus();

		//4 - on bind move
		if (bindMove) {

			//a. unsubscribe listener
			this.main.sliderSubscriptionExtension.unsubscribeOnMove();
			const onMoveCallback: ((e: MouseEvent|TouchEvent) => void) = (e: MouseEvent|TouchEvent) => this.main.dragging.active ? this.onDragMove(e) : this.onMove(e);

			//b. update touch move event
			if (this.main.customizableSliderHelper.isTouchEvent(event)) {
				this.main.onMoveEventListener = this.main.eventListenerHelper.attachPassiveEventListener(
				document, 'touchmove', onMoveCallback);
			} else {
				this.main.onMoveEventListener = this.main.eventListenerHelper.attachEventListener(
				document, 'mousemove', onMoveCallback);
			}
		}

		//5 - on bind end event
		if (bindEnd) {

			//a. unsubscribe listener
			this.main.sliderSubscriptionExtension.unsubscribeOnEnd();
			const onEndCallback: ((e: MouseEvent|TouchEvent) => void) = (e: MouseEvent|TouchEvent) => this.onEnd(e);

			//b. attach new listener
			if (this.main.customizableSliderHelper.isTouchEvent(event)) this.main.onEndEventListener = this.main.eventListenerHelper.attachPassiveEventListener(document, 'touchend', onEndCallback);
				else this.main.onEndEventListener = this.main.eventListenerHelper.attachEventListener(document, 'mouseup', onEndCallback);
		}

		//6 - emit change context event
		this.main.userChangeStart.emit(this.main.sliderHelperExtension.getChangeContext());

		//7 - update touch id and simulate evens
		if (this.main.customizableSliderHelper.isTouchEvent(event) && Util.Basic.isDefined((event as TouchEvent).changedTouches) && Util.Basic.isUndefined(this.main.touchId)) this.main.touchId = (event as TouchEvent).changedTouches[0].identifier;
		if (simulateImmediateMove) 	this.onMove(event, true);
		if (simulateImmediateEnd)	this.onEnd(event);
	}

	onPointerFocus(pointerType: EnumNgPointerType) {

		//0 - on subscribe add event
		const pointerElement: NgSliderHandleDirective = this.main.getPointerElement(pointerType);
		if (!pointerElement) return;
		pointerElement.on('blur', 	() => this.onPointerBlur(pointerElement));
		pointerElement.on('keyup', 	() => this.onKeyUp());
		pointerElement.on('keydown', (event: KeyboardEvent) => this.onKeyboardEvent(event));
		pointerElement.active = true;

		//1 - update pointer and key events
		this.main.currentTrackingPointer = pointerType;
		this.main.currentFocusPointer 	 = pointerType;
		this.main.firstKeyDown 			 = true;
	}

	onDragStart(pointerType: EnumNgPointerType, event: MouseEvent|TouchEvent, bindMove: boolean, bindEnd: boolean) {

		//0 - get event position
		const position: number = this.main.sliderHelperExtension.getEventPosition(event);

		//1 - update dragging event
		this.main.dragging 		 	  = new NgDragging();
		this.main.dragging.active 	  = true;
		this.main.dragging.value 	  = this.main.sliderHelperExtension.positionToValue(position);
		this.main.dragging.difference = this.main.viewHighValue - this.main.viewLowValue;

		this.main.dragging.lowLimit = this.main.viewOptions.rightToLeft
			? this.main.minHandleElement.position - position
			: position - this.main.minHandleElement.position;

		this.main.dragging.highLimit = this.main.viewOptions.rightToLeft
			? position - this.main.maxHandleElement.position
			: this.main.maxHandleElement.position - position;

		//2 - update on start event
		this.onStart(pointerType, event, bindMove, bindEnd);
	}

	/**------------------------------------------------------
	 * Helper Function
	 */
	private onMove(event: MouseEvent|TouchEvent, fromTick?: boolean) {

		//0 - set touch for slider status
		let touchForThisSlider: Touch = null!;
		if (this.main.customizableSliderHelper.isTouchEvent(event)) {

			//a. get touch changes
			const changedTouches: TouchList = (event as TouchEvent).changedTouches;

			//b. update touches for the slider
			for (let i: number = 0; i < changedTouches.length; i++) {

				// continue on unmatched identifier
				if (changedTouches[i].identifier !== this.main.touchId) continue;

				// update slider
				touchForThisSlider = changedTouches[i];
				break;
			}

			//c. return on undefined value of this slider
			if (Util.Basic.isUndefined(touchForThisSlider)) return;
		}

		//1 - update animate class
		if (this.main.viewOptions.animate && !this.main.viewOptions.animateOnMove && this.main.moving) this.main.sliderElementAnimateClass = false;

		//2 - update new position
		this.main.moving = true;
		const newPos	: number = Util.Basic.isDefined(touchForThisSlider)
			? this.main.sliderHelperExtension.getEventPosition(event, touchForThisSlider.identifier)
			: this.main.sliderHelperExtension.getEventPosition(event);
		const ceilValue	: number = this.main.viewOptions.rightToLeft ? this.main.viewOptions.floor! : this.main.viewOptions.ceil!;
		const floorValue: number = this.main.viewOptions.rightToLeft ? this.main.viewOptions.ceil! 	: this.main.viewOptions.floor!;

		//3 - update new value
		let newValue: number;
		if (newPos <= 0) {
			newValue = floorValue;
		} else if (newPos >= this.main.maxHandlePosition) {
			newValue = ceilValue;
		} else {
			const positionToValue: number = this.main.sliderHelperExtension.positionToValue(newPos);
			newValue = (fromTick && Util.Basic.isDefined(this.main.viewOptions.tickStep))
				? this.main.sliderHelperExtension.roundStep(positionToValue, this.main.viewOptions.tickStep)
				: this.main.sliderHelperExtension.roundStep(positionToValue);
		}

		//4 - handle position tracking handle
		this.main.sliderHelperExtension.positionTrackingHandle(newValue);
	}

	private onEnd(event: MouseEvent|TouchEvent) {

		//0 - check if slider touch event
		if (this.main.customizableSliderHelper.isTouchEvent(event)) {
			const changedTouches: TouchList = (event as TouchEvent).changedTouches;
			if (changedTouches[0].identifier !== this.main.touchId) return;
		}

		//1 - update moving status
		this.main.moving = false;
		if (this.main.viewOptions.animate) this.main.sliderElementAnimateClass = true;

		//2 - reset touch element and handle
		this.main.touchId = null!;
		if (!this.main.viewOptions.keyboardSupport) {
			this.main.minHandleElement.active = false;
			this.main.maxHandleElement.active = false;
			this.main.currentTrackingPointer  = null!;
		}

		//3 - reset dragging and unsubscribe listener
		this.main.dragging.active = false;
		this.main.sliderSubscriptionExtension.unsubscribeOnMove();
		this.main.sliderSubscriptionExtension.unsubscribeOnEnd();

		//4 - update context
		this.main.userChangeEnd.emit(this.main.sliderHelperExtension.getChangeContext());
	}

	private onKeyUp() {
		this.main.firstKeyDown = true;
		this.main.userChangeEnd.emit(this.main.sliderHelperExtension.getChangeContext());
	}

	private onPointerBlur(pointer: NgSliderHandleDirective) {

		//0 - turn of the pointer event
		pointer.off('blur');
		pointer.off('keydown');
		pointer.off('keyup');
		pointer.active = false;

		//1 - reset pointer
		if (Util.Basic.isUndefined(this.main.touchId)) {
			this.main.currentTrackingPointer = null!;
			this.main.currentFocusPointer 	 = null!;
		}
	}

	private getKeyActions(currentValue: number): {[key: string]: number} {

		//0 - set key actions step and page
		const valueRange: number = this.main.viewOptions.ceil! - this.main.viewOptions.floor!;
		let increaseStep: number = currentValue + this.main.viewOptions.step!;
		let decreaseStep: number = currentValue - this.main.viewOptions.step!;
		let increasePage: number = currentValue + valueRange / 10;
		let decreasePage: number = currentValue - valueRange / 10;

		//1 - if reversed controls then update page and step
		if (this.main.viewOptions.reversedControls) {
			increaseStep = currentValue - this.main.viewOptions.step!;
			decreaseStep = currentValue + this.main.viewOptions.step!;
			increasePage = currentValue - valueRange / 10;
			decreasePage = currentValue + valueRange / 10;
		}

		//2 - Left to right default actions
		const actions: {[key: string]: number} = {
			UP		: increaseStep,
			DOWN	: decreaseStep,
			LEFT	: decreaseStep,
			RIGHT	: increaseStep,
			PAGEUP	: increasePage,
			PAGEDOWN: decreasePage,
			HOME	: this.main.viewOptions.reversedControls ? this.main.viewOptions.ceil! 	: this.main.viewOptions.floor!,
			END		: this.main.viewOptions.reversedControls ? this.main.viewOptions.floor! : this.main.viewOptions.ceil!,
		}

		//3 - right to left means swapping right and left arrows
		if (this.main.viewOptions.rightToLeft) {

			//a. set actions steps
			(<any>actions).LEFT = increaseStep;
			(<any>actions).RIGHT = decreaseStep;

			//b right to left and vertical means we also swap up and down
			if (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0) {
				(<any>actions).UP = decreaseStep;
				(<any>actions).DOWN = increaseStep;
			}
		}
		return actions;
	}

	private onKeyboardEvent(event: KeyboardEvent) {

		//0 get current key value
		const currentValue	: number = this.main.sliderHelperExtension.getCurrentTrackingValue();
		const keyCode		: number = Util.Basic.isDefined(event.keyCode) ? event.keyCode : event.which;

		//1 - update keys
		const keys: {[keyCode: number]: string} = {
			38: 'UP',
			40: 'DOWN',
			37: 'LEFT',
			39: 'RIGHT',
			33: 'PAGEUP',
			34: 'PAGEDOWN',
			36: 'HOME',
			35: 'END',
		};

		//2 - get actions and bind key, action
		const actions: {[key: string]: number} = this.getKeyActions(currentValue);
		const key	 : string = keys[keyCode];
		const action : number = actions[key];

		//3 - exit on undefined action
		if (Util.Basic.isUndefined(action) || Util.Basic.isUndefined(this.main.currentTrackingPointer)) return;
		event.preventDefault();

		//4 - on first key stroke
		if (this.main.firstKeyDown) {
			this.main.firstKeyDown = false;
			this.main.userChangeStart.emit(this.main.sliderHelperExtension.getChangeContext());
		}

		//5 - action and new value
		const actionValue: number = this.main.customizableSliderHelper.clampToRange(action, this.main.viewOptions.floor!, this.main.viewOptions.ceil!);
		const newValue: number = this.main.sliderHelperExtension.roundStep(actionValue);
		if (!this.main.viewOptions.draggableRangeOnly) {
			this.main.sliderHelperExtension.positionTrackingHandle(newValue);
		} else {

			//a. get difference
			const difference: number = this.main.viewHighValue - this.main.viewLowValue;
			let newMinValue	: number;
			let newMaxValue	: number;

			//b. update value on pointer min and max
			if (this.main.currentTrackingPointer === EnumNgPointerType.Min) {
				newMinValue = newValue;
				newMaxValue = newValue + difference;
				if (newMaxValue > this.main.viewOptions.ceil!) {
					newMaxValue = this.main.viewOptions.ceil!;
					newMinValue = newMaxValue - difference;
				}
			} else if (this.main.currentTrackingPointer === EnumNgPointerType.Max) {
				newMaxValue = newValue;
				newMinValue = newValue - difference;
				if (newMinValue < this.main.viewOptions.floor!) {
					newMinValue = this.main.viewOptions.floor!;
					newMaxValue = newMinValue + difference;
				}
			}

			//c. update position tracker
			this.main.sliderHelperExtension.positionTrackingBar(newMinValue!, newMaxValue!);
		}
	}

	private onDragMove(event?: MouseEvent|TouchEvent) {

		//0 - get new positions and update slider animate class
		const newPos: number = this.main.sliderHelperExtension.getEventPosition(event!);
		if (this.main.viewOptions.animate && !this.main.viewOptions.animateOnMove && this.main.moving) this.main.sliderElementAnimateClass = false;
		this.main.moving = true;

		//1 - set limit and element
		let ceilLimit			: number;
		let floorLimit			: number;
		let floorHandleElement	: NgSliderHandleDirective;
		let ceilHandleElement	: NgSliderHandleDirective;
		if (this.main.viewOptions.rightToLeft) {
			ceilLimit 			= this.main.dragging.lowLimit;
			floorLimit 			= this.main.dragging.highLimit;
			floorHandleElement 	= this.main.maxHandleElement;
			ceilHandleElement 	= this.main.minHandleElement;
		} else {
			ceilLimit 			= this.main.dragging.highLimit;
			floorLimit 			= this.main.dragging.lowLimit;
			floorHandleElement 	= this.main.minHandleElement;
			ceilHandleElement 	= this.main.maxHandleElement;
		}

		//2 - update under and over limit
		const isUnderFloorLimit: boolean = (newPos <= floorLimit);
		const isOverCeilLimit: boolean = (newPos >= this.main.maxHandlePosition - ceilLimit);

		//3 - update new min and max
		let newMinValue: number;
		let newMaxValue: number;
		if (isUnderFloorLimit) {
			if (floorHandleElement.position === 0) return;
			newMinValue = this.main.sliderHelperExtension.getMinValue(newPos, true, false);
			newMaxValue = this.main.sliderHelperExtension.getMaxValue(newPos, true, false);
		} else if (isOverCeilLimit) {
			if (ceilHandleElement.position === this.main.maxHandlePosition) return;
			newMaxValue = this.main.sliderHelperExtension.getMaxValue(newPos, true, true);
			newMinValue = this.main.sliderHelperExtension.getMinValue(newPos, true, true);
		} else {
			newMinValue = this.main.sliderHelperExtension.getMinValue(newPos, false, false);
			newMaxValue = this.main.sliderHelperExtension.getMaxValue(newPos, false, false);
		}

		//4 - update position tracking bar
		this.main.sliderHelperExtension.positionTrackingBar(newMinValue, newMaxValue);
	}
}
